<template>
    <Modal ref="modal" class="re-ordering-modal" @closed="onCloseModal">
        <template #button="sp">
            <button class="btn btn-outline-secondary" @click="sp.showModal">
                Reprendre une commande
            </button>
        </template>
        <template #header="sp">
            <div class="h3">Reprendre une ancienne commande du client</div>
        </template>
        <template #body="sp">
            <ErrorsZone :errors="errors" />

            <LastCustomerOrdersList
                :customer="customer"
                :previous-orders-action="previousOrdersAction"
                @selected="onSelectOrder"
            />

            <div v-if="orderDisplayed" class="mt-3">
                <h4>Contenu de cette commande</h4>
                <table class="table table-sm table-striped" :class="{'table-bordered': isPickupPointProductionOrder}">
                    <thead v-if="isPickupPointProductionOrder">
                        <tr>
                            <th>Commandés</th>
                            <th class="text-center">Préparés</th>
                            <th class="text-center">Retours</th>
                            <th>Produit</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="product in listedProducts">
                            <td v-if="isPickupPointProductionOrder" class="w-10 align-middle text-right">
                                <InputIntegerPositive class="form-control text-right" :allow-zero="true" v-model="orderedProducts[product.sku]"/>
                            </td>
                            <td v-else class="w-10 align-middle text-right">
                                {{ orderedProducts[product.sku] }}x
                            </td>

                            <td v-if="isPickupPointProductionOrder" class="w-10 align-middle text-center">
                                {{ preparedProductsQuantitiesBySku[product.sku] || 0 }}
                            </td>

                            <td v-if="isPickupPointProductionOrder" class="w-10 align-middle text-center">
                                {{ returnedProductsQuantitiesBySku[product.sku] || 0 }}
                            </td>
                            <td v-if="!products[product.sku] || products[product.sku].authorization === undefined || products[product.sku].authorization === false" class="align-middle text-danger">
                                <del>{{ product.short_name }}</del>
                                <span v-if="!hideNotAuthorizedProductsWarnings" class="text-muted">Ce produit n'est pas disponible</span>
                            </td>
                            <td v-else class="align-middle">
                                {{ product.short_name }}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div v-if="isPickupPointProductionOrder" class="mt-2">
                <textarea class="form-control w-100" v-model="comment" ref="commentTextarea" @keydown="avoidLineBreaks" placeholder="Ajouter un commentaire"></textarea>
            </div>
        </template>
        <template #footer>
            <button class="btn btn-outline-dark" @click="closeModal">
                Annuler
            </button>
            <button class="btn btn-success" @click="submit" :disabled="orderDisplayed === null">
                Reprendre cette commande
            </button>
        </template>
    </Modal>
</template>

<script>
    import ErrorsZone from "./ErrorsZone";
    import InputIntegerPositive from "./InputIntegerPositive"
    import LastCustomerOrdersList from "./LastCustomerOrdersList"
    import Modal from "./Modal";

    import {useGetLoader} from "./compositions/axios/loadingRequest";
    import {autoSizeTextarea, avoidLineBreaks} from "../textarea-autoresize";

    export default {
        components: {
            ErrorsZone,
            InputIntegerPositive,
            LastCustomerOrdersList,
            Modal,
        },

        setup() {
            const {
                loading,
                errors,
                load,
            } = useGetLoader();

            return {
                loading,
                errors,
                load,
            };
        },

        props: {
            customer: {
                type: Object,
                required: true,
            },
            products: {
                type: Object,
                required: true,
            },
            previousOrdersAction: {
                type: String,
                required: false,
            },
            hideNotAuthorizedProductsWarnings: {
                type: Boolean,
                required: false,
                default: false,
            },
        },

        data() {
            return {
                orderDisplayed: null,
                orderedProducts: {},
                comment: "",
            };
        },

        methods: {
            submit() {
                if (this.orderDisplayed && this.orderedProducts && confirm("Etes-vous sûr de vouloir reprendre cette commande ? Tous les produits actuellement sélectionnés seront perdus !")) {
                    this.$emit('submit', {
                        orderedProducts: this.orderedProducts,
                        comment: this.comment,
                    });
                    this.closeModal();
                }
            },

            onSelectOrder(orderSelected) {
                if (!orderSelected) {
                    this.orderDisplayed = null;
                    this.orderedProducts = {};
                    return;
                }

                this.orderDisplayed = orderSelected;
                this.orderedProducts = {};
                for (const product of this.orderDisplayed.ordered_products) {
                    this.orderedProducts[product.sku] = product.pivot.quantity;
                }
                if (this.isPickupPointProductionOrder && this.orderDisplayed.prepared_products) {
                    // List prepared products not ordered
                    for (const product of this.orderDisplayed.prepared_products) {
                        if (!this.orderedProducts.hasOwnProperty(product.sku)) {
                            this.orderedProducts[product.sku] = 0;
                        }
                    }
                }
            },

            showModal() {
                this.$refs.modal.showModal();
            },

            closeModal() {
                this.$refs.modal.closeModal();
            },

            onCloseModal() {
                this.orderDisplayed = null;
                this.orderedProducts = {};
                this.comment = "";
                this.$emit('closed');
            },

            avoidLineBreaks,
        },

        watch: {
            comment(newValue, oldValue) {
                this.comment = newValue.replaceAll(/[\n]+/g, ' ');
                this.$nextTick(() => {
                    if (this.$refs.commentTextarea) {
                        autoSizeTextarea(this.$refs.commentTextarea);
                    }
                    const modalBody = this.$el.querySelector('.modal-body');
                    modalBody.scrollTop = modalBody.scrollHeight;
                })
            }
        },

        computed: {
            listedProducts() {
                let products = {};
                if (this.orderDisplayed) {
                    products = _.keyBy(this.orderDisplayed.ordered_products || [], 'sku');
                    if (this.isPickupPointProductionOrder && this.orderDisplayed.prepared_products) {
                        // List prepared products not ordered
                        for (const product of this.orderDisplayed.prepared_products) {
                            if (!products.hasOwnProperty(product.sku)) {
                                products[product.sku] = product;
                            }
                        }
                    }
                }
                return products;
            },

            returnedProductsQuantitiesBySku() {
                let products = {};
                if (this.orderDisplayed && this.orderDisplayed.returned_products) {
                    for (const product of this.orderDisplayed.returned_products) {
                        products[product.sku] = product.pivot.quantity;
                    }
                }
                return products;
            },

            preparedProductsQuantitiesBySku() {
                let products = {};
                if (this.orderDisplayed && this.orderDisplayed.prepared_products) {
                    for (const product of this.orderDisplayed.prepared_products) {
                        products[product.sku] = (products[product.sku] || 0) + product.pivot.quantity;
                    }
                }
                return products;
            },

            isPickupPointProductionOrder() {
                return this.orderDisplayed && this.orderDisplayed.final_customer_type && this.orderDisplayed.final_customer_type === 'pickup_point';
            },
        },
    }
</script>

<style lang="scss">
.re-ordering-modal {
    .modal-container {
        max-width: 90%;
        width: auto;
    }

    .modal-body {
        margin: 0;
    }
}
</style>
